<script setup lang="ts">
import { LinkItemType, type LinkItemDto } from '@swipe4work/models-orval'
import { linkItemTypeAccent, linkItemTypeIcon, linkItemTypeLabel } from '@/features/dashboard/linkItemTypes'
import { useComposedMenu, type DashboardSection } from '@/features/dashboard/composables/UseComposedMenu'
import { useMenuPreferences } from '@/shared/composables/UseMenuPreferences'
import { usePermissions } from '@/shared/composables/UsePermissions'

definePage({
  path: '/homepage',
  name: '/homepage/Homepage',
  alias: [ '/', '/homepage/overview' ],
  meta: { title: 'Homepage' },
})

const route = useRoute()
const router = useRouter()
const { isGlobal } = usePermissions()
const { isPending, allItems, categories, allDashboardSections } = useComposedMenu()
const { preferences } = useMenuPreferences()

function parseCategoryParam(raw: unknown): number | 'all' {
  const value = Array.isArray(raw) ? raw[0] : raw
  if (!value || value === 'all') {
    return 'all'
  }
  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : 'all'
}

/**
 * Initial filter: an explicit `?category=` query param wins (including `all`); otherwise fall back to
 * the configured single-category menu preference (e.g. the chosen department) so the user lands on
 * their category.
 */
function initialCategory(): number | 'all' {
  const raw = Array.isArray(route.query.category) ? route.query.category[0] : route.query.category
  if (raw != null) {
    return parseCategoryParam(raw)
  }

  return preferences.value.categoryIds.length === 1 ? preferences.value.categoryIds[0] : 'all'
}

const selectedCategory = ref<number | 'all'>(initialCategory())

/*
 * Always write the param — also for 'all' — so back-navigation restores the choice instead of
 * falling back to the menu preference.
 */
watch(selectedCategory, (value) => {
  router.replace({
    query: {
      ...route.query,
      category: String(value),
    },
  })
})

const categoryOptions = computed(() => [
  {
    label: 'Alle',
    value: 'all' as const,
  },
  // "Instellingen" always sorts last (stable sort keeps the rest in SortOrder).
  ...[ ...categories.value ]
    .sort((a, b) => Number(a.name === 'Instellingen') - Number(b.name === 'Instellingen'))
    .map(c => ({
      label: c.name,
      value: c.id,
    })),
])

/** All items in a category, grouped by subcategory and ordered (independent of the menu composition). */
function buildCategorySections(categoryId: number): DashboardSection[] {
  const grouped = new Map<string, {
    order: number,
    entries: {
      item: LinkItemDto,
      order: number
    }[]
  }>()

  for (const item of allItems.value) {
    const link = item.categories.find(c => c.id === categoryId)
    if (!link) continue

    const subcategory = link.subcategory?.trim() || 'Overig'
    const bucket = grouped.get(subcategory) ?? {
      order: link.subcategoryOrder,
      entries: [],
    }

    bucket.order = Math.min(bucket.order, link.subcategoryOrder)
    bucket.entries.push({
      item,
      order: link.itemOrder,
    })
    grouped.set(subcategory, bucket)
  }

  return [ ...grouped.entries() ]
    .sort(([ aName, a ], [ bName, b ]) => a.order - b.order || aName.localeCompare(bName))
    .map(([ label, bucket ]) => ({
      key: `sub-${label}`,
      label,
      items: bucket.entries.sort((x, y) => x.order - y.order).map(e => e.item),
    }))
}

const filteredSections = computed(() => {
  // "Alle" → really everything (deduped like the menu); a specific category → every item in that category.
  if (selectedCategory.value === 'all') {
    return allDashboardSections.value
  }

  return buildCategorySections(selectedCategory.value)
})

function openItem(item: LinkItemDto) {
  if (item.type === LinkItemType.InternalLink) {
    if (item.routeName) {
      router.push({ name: item.routeName as any })
    }

    return
  }

  if (item.type === LinkItemType.Uri) {
    if (!item.uri) return
    if (item.openInNewTab === false) {
      window.location.href = item.uri
    }
    else {
      window.open(item.uri, '_blank', 'noopener')
    }

    return
  }

  router.push({
    name: '/dashboard/DashboardDetail' as any,
    params: { id: item.id },
  })
}
</script>

<template>
  <div class="space-y-6">  
    <div v-if="isPending" class="grid grid-cols-1 gap-4 lg:grid-cols-3 md:grid-cols-2">
      <Skeleton v-for="n in 6" :key="n" height="9rem" />
    </div>

    <template v-else>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div role="radiogroup" aria-label="Categorie" class="flex flex-wrap items-center gap-2">
          <Button
            v-for="option in categoryOptions"
            :key="String(option.value)"
            role="radio"
            :aria-checked="selectedCategory === option.value"
            :label="option.label"
            rounded
            size="small"
            :severity="selectedCategory === option.value ? undefined : 'secondary'"
            :outlined="selectedCategory !== option.value"
            :class="selectedCategory === option.value ? undefined : '!bg-surface-0'"
            @click="selectedCategory = option.value"
          />
        </div>

        <Button
          v-if="isGlobal"
          label="Beheer"
          icon="fa-regular fa-gear"
          severity="secondary"
          outlined
          size="small"
          @click="router.push({ name: '/homepage/beheer' as any })"
        />
      </div>

      <Message v-if="filteredSections.length === 0" severity="secondary">
        Geen links in deze categorie.
      </Message>

      <section v-for="section in filteredSections" :key="section.key" class="space-y-3">
        <div class="flex items-center gap-3">
          <h6 class="m-0 text-xs text-surface-400 font-semibold tracking-wider uppercase">
            {{ section.label }}
          </h6>
          <span class="h-px flex-1 bg-surface-200" />
          <span class="px-2 py-0.5 text-xs text-[#BCC2D4] font-medium tracking-wide">
            {{ section.items.length }} links
          </span>
        </div>

        <div class="grid auto-rows-fr grid-cols-1 gap-4 lg:grid-cols-3 md:grid-cols-2">
          <Card
            v-for="item in section.items"
            :key="item.id"
            class="group relative h-full transform cursor-pointer border border-r-4 border-surface-200 transition-all transition-duration-300 hover:(transform-translate-y--0.5 shadow-xl)"
            :class="linkItemTypeAccent(item.type).border"
            :pt="{ body: { class: 'h-full' }, content: { class: 'h-full' } }"
            @click="openItem(item)"
          >
            <template #content>
              <div class="h-full flex flex-col gap-3">
                <div class="flex items-start gap-3">
                  <span
                    class="h-10 w-10 flex shrink-0 items-center justify-center rounded-lg transition-colors"
                    :class="linkItemTypeAccent(item.type).icon"
                  >
                    <i :class="item.icon || linkItemTypeIcon(item.type)" class="text-lg" />
                  </span>
                  <i
                    :class="item.type === LinkItemType.Uri
                      ? 'fa-regular fa-arrow-up-right-from-square'
                      : item.type === LinkItemType.InternalLink
                        ? 'fa-regular fa-arrow-right'
                        : 'fa-regular fa-chevron-right'"
                    class="absolute right-3 top-3 shrink-0 text-xs text-surface-300 opacity-0 transition-colors transition-opacity transition-duration-300 group-hover:(block text-primary-600 opacity-100)"
                  />
                </div>
                <div class="">
                  <span class="block truncate text-base text-surface-900 font-semibold leading-tight" :title="item.name">{{ item.name }}</span>
                  <p v-if="item.description" class="line-clamp-2 m-0 mt-1 text-sm text-surface-500">
                    {{ item.description }}
                  </p>
                </div>
                <div class="mb-auto flex flex-wrap items-center gap-1.5">
                  <Tag
                    v-if="item.type !== LinkItemType.InternalLink"
                    :value="linkItemTypeLabel(item.type)"
                    :severity="linkItemTypeAccent(item.type).tagSeverity"
                    :pt="{
                      label: {
                        class: 'text-xs',
                      },
                    }"
                  />
                  <Tag
                    v-for="category in item.categories"
                    :key="category.id"
                    :value="category.name"
                    :pt="{
                      label: {
                        class: 'text-xs',
                      },
                    }"
                    severity="secondary"
                  />
                </div>
              </div>
            </template>
          </Card>
        </div>
      </section>
    </template>
  </div>
</template>
